import _, { compact, uniq } from "lodash"
import { useEffect, useCallback, useMemo, useRef } from "react"
import { Id, featureCollection } from "@turf/helpers"
import { useVenue } from "providers/venue"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import {
  getDuplexGeometryIdsByFeature,
  getLocationIdByFeature,
  getRelatedLocationIdsByFeature,
  getSuitablyValueBetweenBearings,
  getBearingBetweenPoints,
} from "components/IndoorMap"
import { IRoute } from "interfaces"
import { isArrayOfLngLat, sampleLineStringByDistance } from "./utils"
import { getCenterFromGeometry } from "utils/geometry"

const DEFAULT_OPTIONS = {
  offset: { top: 0, left: 0, right: 0, bottom: 0 },
  enabled: true,
}

type Options = {
  enabled?: boolean
  offset?: {
    bottom?: number
    left?: number
    right?: number
    top?: number
  }
}

type UseStepByStepType = (
  route: IRoute | null,
  stepNumber: number,
  options: Options
) => void

const resolveStepBearing = ({
  hasGeolocationOrigin,
  instructionBearing,
  defaultBearing,
  pathBearing,
  currentBearing,
}) => {
  const targetBearing =
    hasGeolocationOrigin && instructionBearing === undefined
      ? defaultBearing
      : instructionBearing ?? pathBearing
  return getSuitablyValueBetweenBearings(targetBearing, currentBearing)
}

export const useStepByStepMap: UseStepByStepType = (
  route,
  stepNumber,
  options = DEFAULT_OPTIONS
) => {
  const { indoorRef, mapLoaded } = useIndoorMap()
  const {
    amenities,
    dataLoaded,
    transformStepsToGeometries,
    transformLocationToFeature,
    relationshipGraphLoaded,
    stepAutoplayConfig,
    mapConfig,
  } = useVenue()

  const isOriginMarkerSpriteModeEnabled =
    _.get(mapConfig, "origin_marker_mode") === "sprite-marker"

  const { offset, enabled } = options
  const { bottom, top, left, right } = offset
  const offsetOption = useMemo(
    () => ({ bottom, top, left, right }),
    [bottom, top, left, right]
  )
  const getAmenityIdsFromUnitIds = useCallback(
    (unitIds: Id[]) => {
      const matchingAmenities = amenities.filter((amenity) =>
        amenity.properties?.unit_ids?.some((id) => unitIds?.includes(id))
      )
      return matchingAmenities.map((amenity) => amenity.id)
    },
    [amenities]
  )
  const mapReady =
    indoorRef?.current && mapLoaded && dataLoaded && relationshipGraphLoaded

  const navigationGeometries = useMemo(
    () => (route?.steps ? transformStepsToGeometries(route?.steps) : []),
    [route?.steps, transformStepsToGeometries]
  )

  const targetDestination = useMemo(() => {
    const targetLocationId = _.get(
      route,
      "destination.properties.target_location_id"
    )
    return transformLocationToFeature(targetLocationId)
  }, [route, transformLocationToFeature])

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
  const animationRunIdRef = useRef(0)
  const animateOriginMarker = async (coords, isLastStep, arrivalBearing) => {
    const runId = ++animationRunIdRef.current
    if (!isArrayOfLngLat(coords)) {
      indoorRef.current.changeOriginMarkerCoordinate(coords)
      if (isLastStep) {
        indoorRef.current.faceOriginMarkerSpriteToCamera(arrivalBearing)
      }
      return
    }
    const sampleLineString = sampleLineStringByDistance(coords)
    const steps = sampleLineString.length - 1
    const stepDelay = stepAutoplayConfig.delay / steps
    for (let i = 0; i < steps; i++) {
      if (runId !== animationRunIdRef.current) return
      indoorRef.current.animateOriginMarker(
        sampleLineString[i],
        sampleLineString[i + 1],
        stepDelay
      )
      await delay(stepDelay)
    }
    if (isLastStep && runId === animationRunIdRef.current) {
      indoorRef.current.faceOriginMarkerSpriteToCamera(arrivalBearing)
    }
  }

  // Effect#1: Prepare indoor map before drawing route path
  useEffect(() => {
    const indoorMap = indoorRef?.current
    if (indoorMap && mapLoaded) {
      indoorMap.hideVenueObjects()
      indoorMap.disableClick()
      //set opacity of extrudedUnits to 0.75
      indoorMap?.setFeatureObject3DsOpacity(0.75)
    }
    return () => {
      indoorMap?.setFeatureObject3DsOpacity(1)
      indoorMap?.setSpriteMarkersOpacity(1)
    }
  }, [indoorRef, mapLoaded])

  // Effect#2: Create new route path on indoor map
  useEffect(() => {
    if (mapReady && enabled && route) {
      indoorRef.current.clearNavigationGeometries()
      indoorRef.current.createNavigationGeometries(
        navigationGeometries,
        route.destination
      )
    }
  }, [indoorRef, navigationGeometries, mapReady, enabled, route])

  /**
   * Effect #3: Update the view for the current navigation step:
   * 1) Highlight the destination unit if it exists
   * 2) Highlight vertical transition markers (elevator, escalator, stairs)
   * 3) Move the camera along the step route path
   * 4) Animate the origin marker along the step path
   * 5) Hide origin and destination labels (logo/text markers)
   */
  useEffect(() => {
    if (mapReady && enabled) {
      const indoorMap = indoorRef.current
      // handle the step page
      const targetStepGeoJSON = navigationGeometries.find(
        ({ id }) => id === `step-${stepNumber}`
      )

      if (!targetStepGeoJSON) return

      indoorRef.current.clearHighlightObject()
      indoorRef.current.clearHighlightElements()

      const {
        properties,
        isVerticalStep,
        isLastStep,
        geometry,
        hasGeolocationOrigin,
        bearing: instructionBearing,
      } = targetStepGeoJSON
      const { ordinal: currentStepOrdinal, intermediaryIds } = properties

      //only show geometry, path and maker in current ordinal
      indoorRef.current.setOriginMarkerOrdinal(currentStepOrdinal)
      indoorRef.current.changeLevelByOrdinal(currentStepOrdinal)

      // 1) Highlight the destination unit if the current step includes one
      const hasLocatedDestination =
        currentStepOrdinal === targetDestination.properties?.ordinal
      if (hasLocatedDestination) {
        // 1.1) Use the main location from the target destination
        const mainLocationIdFromTargetDest =
          getLocationIdByFeature(targetDestination)
        // 1.2) Avoid redundant highlighting for occupants and ignore geolocation cases
        const targetDestinationId = ["geolocation", "occupant"].includes(
          targetDestination?.feature_type
        )
          ? null
          : targetDestination?.id
        // 1.3) Include related location IDs from occupants (e.g., duplex units on the same floor)
        const relatedLocationIdsFromTargetDest =
          targetDestination?.feature_type !== "section"
            ? getRelatedLocationIdsByFeature(targetDestination)
            : []

        const normalizedDestElementIds = compact(
          uniq([
            targetDestinationId,
            mainLocationIdFromTargetDest,
            ...relatedLocationIdsFromTargetDest,
          ])
        )

        if (normalizedDestElementIds.length > 0) {
          indoorRef.current.setHighlightedObject(normalizedDestElementIds)
        }
      }

      // 2) Highlight elevator, escalator, and stair markers for vertical steps
      if (isVerticalStep) {
        // Vertical step intermediaries include elevators, escalators, and stairs
        const amenitiesToHighlight = getAmenityIdsFromUnitIds(intermediaryIds)
        indoorRef.current.setHighlightedObject(amenitiesToHighlight)
      }

      // 3) Handle map view for each navigation step
      let lastStepArrivalBearing: number | undefined
      // 3.1) Fly to and zoom in on the target for vertical steps
      if (isVerticalStep || (isLastStep && !isOriginMarkerSpriteModeEnabled)) {
        const targetPosition = getCenterFromGeometry(geometry)
        indoorRef.current.flyToAndZoomIn(targetPosition)
      } else if (isLastStep) {
        //calculate sprite facing on last step
        const targetPosition = getCenterFromGeometry(geometry)
        const currentBearing = indoorRef.current.getBearing()
        const defaultBearing = indoorRef.current.defaultBearing

        const previousStepGeoJSON = navigationGeometries.find(
          ({ id }) => id === `step-${stepNumber - 1}`
        )
        const previousStepCoords = previousStepGeoJSON?.geometry?.coordinates
        const previousStepEndpoint = isArrayOfLngLat(previousStepCoords)
          ? previousStepCoords[previousStepCoords.length - 1]
          : previousStepCoords
        const arrivalPoint = geometry.coordinates
        const pathBearing =
          previousStepEndpoint &&
          getBearingBetweenPoints(
            { x: previousStepEndpoint[0], y: previousStepEndpoint[1] },
            { x: arrivalPoint[0], y: arrivalPoint[1] }
          )

        const bearing = resolveStepBearing({
          hasGeolocationOrigin,
          instructionBearing,
          defaultBearing,
          pathBearing,
          currentBearing,
        })
        lastStepArrivalBearing = bearing
        indoorRef.current.flyToAndZoomIn(targetPosition, { bearing })
      } else {
        // 3.2) adjust map view to saw entire step path on current step
        const navigationCollection = featureCollection([targetStepGeoJSON])
        const extent = indoorRef.current.getFeatureExtent(navigationCollection)
        const center = indoorRef.current.getExtentCenter(extent)
        const zoom = indoorRef.current.getExtentZoom(extent)
        const pitch = 60
        const currentBearing = indoorRef.current.getBearing()
        const defaultBearing = indoorRef.current.defaultBearing
        const pathBearing =
          indoorRef.current.getLineStringBearing(targetStepGeoJSON)

        const bearing = resolveStepBearing({
          hasGeolocationOrigin,
          instructionBearing,
          defaultBearing,
          pathBearing,
          currentBearing,
        })

        const newCenter = indoorMap.getTargetViewCenter(
          {
            center,
            zoom,
            pitch,
            bearing,
          },
          { offset: offsetOption }
        )

        indoorRef.current.flyTo(newCenter, {
          zoom,
          bearing,
          pitch,
          duration: 600,
          ease: "out",
        })
      }

      // 4) Animate the origin marker along the step path
      if (isOriginMarkerSpriteModeEnabled) {
        const coords = targetStepGeoJSON.geometry.coordinates
        animateOriginMarker(coords, isLastStep, lastStepArrivalBearing)
        indoorRef.current.setDestinationMarkerVisible(!isLastStep)
      }

      // 5) Hide origin and destination labels (logo/text markers)
      // TODO: improve logic to show origin logo marker for case both, occupant render_type === logo (mobile)
      // hide origin and destination logo / text marker prevent overlap marker
      const destDuplexIds = getDuplexGeometryIdsByFeature(route.destination)
      const destElementIdsToHide = _.compact([
        route.destination.id,
        ...destDuplexIds,
      ])
      indoorRef.current.hideGeometryByElementIds(destElementIdsToHide)

      const originDuplexIds = getDuplexGeometryIdsByFeature(route.origin)
      const originElementIdsToHide = _.compact([
        route.origin.id,
        ...originDuplexIds,
      ])
      indoorRef.current.hideGeometryByElementIds(originElementIdsToHide)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    indoorRef,
    mapReady,
    navigationGeometries,
    offsetOption,
    targetDestination,
    route?.destination.id,
    route?.origin.id,
    stepNumber,
    getAmenityIdsFromUnitIds,
  ])
}
